import { ListMusic, Play, Pause, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { Playlist } from '@/lib/musicTypes';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { useTrackZaps, formatSats } from '@/hooks/useTrackZaps';
import { nip19 } from 'nostr-tools';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';
import { ZapButton } from './ZapButton';
import { LikeButton } from './LikeButton';
import { usePlaylistTracks } from '@/hooks/usePlaylistTracks';

interface FeaturedPlaylistCardProps {
  playlist: Playlist;
  index?: number;
}

export function FeaturedPlaylistCard({ playlist, index = 0 }: FeaturedPlaylistCardProps) {
  const author = useAuthor(playlist.pubkey);
  const { data: zaps } = useTrackZaps(playlist.event.kind, playlist.pubkey, playlist.id);
  const { data: tracks } = usePlaylistTracks(playlist);
  const { playTrack } = useMusicPlayer();

  const metadata = author.data?.metadata;
  const displayName = metadata?.name || genUserName(playlist.pubkey);

  // Calculate total zap amount
  const totalZaps = zaps?.reduce((sum, zap) => sum + zap.amount, 0) || 0;

  // Create naddr for the playlist
  const naddr = nip19.naddrEncode({
    kind: playlist.event.kind,
    pubkey: playlist.pubkey,
    identifier: playlist.id,
  });

  // Generate gradient background if no image
  const backgroundStyle = playlist.imageUrl
    ? { backgroundImage: `url(${playlist.imageUrl})` }
    : playlist.gradient
    ? { background: `linear-gradient(135deg, ${playlist.gradient.join(', ')})` }
    : { background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.6))' };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't play if clicking on a link or button
    const target = e.target as HTMLElement;
    if (target.closest('a') || target.closest('button')) {
      return;
    }
    if (tracks && tracks.length > 0) {
      playTrack(tracks[0], tracks);
    }
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (tracks && tracks.length > 0) {
      playTrack(tracks[0], tracks);
    }
  };

  return (
    <Card
      className="group overflow-hidden hover:bg-white/[0.07] transition-all cursor-pointer border-0 bg-transparent"
      onClick={handleCardClick}
    >
      <CardContent className="p-4">
        {/* Playlist Cover - Large */}
        <div className="relative aspect-square mb-4 rounded-lg overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-white/[0.08]"
            style={backgroundStyle}
          >
            {/* Show icon on gradient backgrounds (no image) */}
            {!playlist.imageUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <ListMusic className="h-20 w-20 text-white/80 drop-shadow" />
              </div>
            )}
          </div>

          {/* Action Buttons - Bottom Right */}
          <div className="absolute bottom-3 right-3 z-10 flex gap-2" onClick={(e) => e.stopPropagation()}>
            <LikeButton
              event={playlist.event}
              size="icon"
              variant="ghost"
              className="bg-white/[0.07] hover:bg-white/[0.12] backdrop-blur-sm h-10 w-10 rounded-full"
            />
            <ZapButton
              event={playlist.event}
              size="icon"
              variant="ghost"
              className="bg-white/[0.07] hover:bg-white/[0.12] backdrop-blur-sm h-10 w-10 rounded-full"
            />
          </div>

          {/* Play Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
            <Button
              size="icon"
              onClick={handlePlayClick}
              disabled={!tracks || tracks.length === 0}
              className="h-16 w-16 rounded-full bg-white text-black hover:bg-white/90 hover:scale-110 transition-transform disabled:opacity-30 [&_svg]:!text-black"
            >
              <Play className="h-7 w-7 ml-1" />
            </Button>
          </div>
        </div>

        {/* Playlist Info */}
        <div>
          <Link
            to={`/${naddr}`}
            className="block"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-lg line-clamp-2 hover:text-primary transition-colors">
              {playlist.title}
            </h3>
          </Link>
          <p className="text-sm text-muted-foreground line-clamp-1">
            {displayName} • {playlist.trackRefs.length} track{playlist.trackRefs.length !== 1 ? 's' : ''}
          </p>

          {/* Zap Total Badge */}
          {totalZaps > 0 && (
            <div className="pt-2">
              <Badge variant="secondary" className="text-xs font-semibold">
                <Zap className="h-3 w-3 mr-1 fill-current" />
                {formatSats(totalZaps)}
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
